import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Sparkles, Send, FileText, ListOrdered, MessageSquare, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const QUICK = [
  { action: "quote", label: "Generar presupuesto", icon: FileText, placeholder: "Describe el trabajo a presupuestar..." },
  { action: "prioritize", label: "Priorizar avisos", icon: ListOrdered, placeholder: "Pega la lista de avisos a priorizar..." },
  { action: "summarize", label: "Resumir incidencia", icon: MessageSquare, placeholder: "Pega la incidencia a resumir..." },
];

export function AIAssistant({ open, onOpenChange }) {
  const [action, setAction] = useState("chat");
  const [messages, setMessages] = useState([
    { role: "ai", text: "Hola, soy Navirox AI. Puedo generar presupuestos, priorizar avisos, resumir incidencias o responder tus dudas operativas. ¿En qué te ayudo?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/assist", { action, text: userText });
      setMessages((m) => [...m, { role: "ai", text: data.result }]);
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((m) => [...m, { role: "ai", text: "Lo siento, ha ocurrido un error al procesar tu solicitud." }]);
    } finally {
      setLoading(false);
      setAction("chat");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col bg-white p-0 sm:max-w-md" data-testid="ai-assistant-panel">
        <SheetHeader className="border-b border-[#D2D2D7]/50 p-5">
          <SheetTitle className="flex items-center gap-2 font-display text-lg">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#0066CC] to-[#5856D6]">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            Navirox AI
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-wrap gap-2 border-b border-[#D2D2D7]/50 p-4">
          {QUICK.map((q) => (
            <button
              key={q.action}
              data-testid={`ai-quick-${q.action}`}
              onClick={() => { setAction(q.action); setInput(""); }}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                action === q.action
                  ? "border-[#0066CC] bg-[#EAF2FC] text-[#0066CC]"
                  : "border-[#D2D2D7] text-[#1D1D1F]/70 hover:bg-[#F5F5F7]"
              }`}
            >
              <q.icon className="h-3.5 w-3.5" /> {q.label}
            </button>
          ))}
        </div>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${
                  m.role === "user"
                    ? "bg-[#0066CC] text-white"
                    : "bg-[#F5F5F7] text-[#1D1D1F]"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl bg-[#F5F5F7] px-4 py-2.5 text-sm text-[#86868B]">
                <Loader2 className="h-4 w-4 animate-spin" /> Pensando...
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-[#D2D2D7]/50 p-4">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={QUICK.find((q) => q.action === action)?.placeholder || "Escribe tu mensaje..."}
              rows={2}
              className="flex-1 resize-none rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-2.5 text-sm text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
            />
            <button
              data-testid="ai-send"
              onClick={send}
              disabled={loading}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0066CC] text-white transition-all hover:bg-[#004499] disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
